import { useCallback, useState } from "react";
import { useVerbaPagamentoListaPagination } from "../hooks/VerbaPagamento_lista/pagination";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useVerbaPagamentoListaFilter } from "../hooks/VerbaPagamento_lista/filter";
import { VerbaPagamento } from "../../domain/models/verba_pagamento";
import { VerbaPagamentoForm } from "../schemas/VerbaPagamento_lista/verba_pagamento";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";

/**
 * LoadVerbaPagamentosState
 */

type LoadVerbaPagamentosState = {
  tipoPesquisa?: number;
  valueField?: string;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useVerbaPagamentoListaViewModel
 */

export const useVerbaPagamentoListaViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    },
    rh: {
      getAllVerbaPagamentoUseCase,
      createVerbaPagamentoUseCase,
      updateVerbaPagamentoUseCase,
      deleteVerbaPagamentoUseCase,
    },
  } = useDependencyInjection();

  const debouncedRef = useCreateDebounceHandler(750);

  const { pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>();

  const [verbaPagamentos, setVerbaPagamentos] = useState<VerbaPagamento[]>();
  const [selectedVerbaPagamento, setSelectedVerbaPagamento] = useState<VerbaPagamento>();

  const [openCadastroModal, setOpenCadastroModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const filters = useVerbaPagamentoListaFilter();
  const pagination = useVerbaPagamentoListaPagination();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: error => pushErrorNotification("Erro", [error]),
  });

  /**
   * Funções de carregamento
   */

  const loadVerbaPagamentos = useCallback(async (funcState?: LoadVerbaPagamentosState) => {
    try {
      const filteredTipoPesquisa = evaluateParamValue(funcState?.tipoPesquisa, filters.tipoPesquisa);
      const filteredValueField = evaluateParamValue(funcState?.valueField, filters.valueField);

      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllVerbaPagamentoUseCase.getAllVerbaPagamento(
        filteredTipoPesquisa,
        filteredValueField,
        filteredPage,
        filteredRowsPerPage,
      );

      setVerbaPagamentos(getAllVerbaPagamentoUseCase.items);

      useVerbaPagamentoListaPagination.setState({ totalItems: getAllVerbaPagamentoUseCase.totalItems });
    } catch (e: unknown) {
      setVerbaPagamentos([]);
      throw e;
    }
  }, [
    filters,
    pagination,
    getAllVerbaPagamentoUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      setErrorMessage(undefined);
      await loadVerbaPagamentos();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar verba de pagamentos!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleSubmitVerbaPagamento = useCallback(async (form: VerbaPagamentoForm) => {
    try {
      setSaving(true);
      setLoading(true);

      if (selectedVerbaPagamento) {
        await updateVerbaPagamentoUseCase.updateVerbaPagamento(
          selectedVerbaPagamento.idItemVerba,
          form.ativo,
          form.tipo,
          form.nro,
          form.verba,
          form.valor,
        );
      } else {
        await createVerbaPagamentoUseCase.createVerbaPagamento(
          form.ativo,
          form.tipo,
          form.nro,
          form.verba,
          form.valor,
        );
      }
      await loadVerbaPagamentos();
      pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar verba de pagamentos!"));
    } finally {
      setLoading(false);
      setSaving(false);
      setOpenCadastroModal(false);
    }
  }, [
    selectedVerbaPagamento,
    createVerbaPagamentoUseCase,
    updateVerbaPagamentoUseCase,
    pushErrorNotification,
    loadVerbaPagamentos,
    pushInfoNotification,
  ]);

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadVerbaPagamentos();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar verba de pagamentos!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadVerbaPagamentos,
    pushErrorNotification,
  ]);

  const handleCadastroVerbaPagamentoOpenState = useCallback((idItemVerba: number | null, openState: boolean) => {
    if (openState) {
      if (idItemVerba && verbaPagamentos) {
        setSelectedVerbaPagamento(verbaPagamentos.find(x => x.idItemVerba === idItemVerba));
      } else {
        setSelectedVerbaPagamento(undefined);
      }
    }
    setOpenCadastroModal(openState);
  }, [
    verbaPagamentos,
  ]);

  const handleDeleteVerbaPagamentoOpenState = useCallback((idItemVerba: number | null, openState: boolean) => {
    if (openState) {
      if (idItemVerba && verbaPagamentos) {
        setSelectedVerbaPagamento(verbaPagamentos.find(x => x.idItemVerba === idItemVerba));
      } else {
        setSelectedVerbaPagamento(undefined);
      }
    }
    setOpenDeleteModal(openState);
  }, [
    verbaPagamentos,
  ]);

  const handleConfirmarDeletarVerbaPagamento = useCallback(async () => {
    if (selectedVerbaPagamento) {
      try {
        setSaving(true);
        await deleteVerbaPagamentoUseCase.deleteVerbaPagamento(selectedVerbaPagamento.idItemVerba);
        await loadVerbaPagamentos();
        pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Error", processError(e, "Falha ao buscar verba de pagamentos!"));
      } finally {
        setSaving(false);
        setOpenDeleteModal(false);
      }
    }
  }, [
    selectedVerbaPagamento,
    deleteVerbaPagamentoUseCase,
    loadVerbaPagamentos,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleTipoPesquisaChange = useDebouncedCallback(debouncedRef, (tipoPesquisa: number) => {
    useVerbaPagamentoListaFilter.setState({ tipoPesquisa });
    return async () => {
      try {
        setLoading(true);
        useVerbaPagamentoListaPagination.setState({ page: 1 });
        await loadVerbaPagamentos({ tipoPesquisa, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Error", processError(e, "Falha ao buscar verba de pagamentos!"));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadVerbaPagamentos,
    pushErrorNotification,
  ]);

  const handleValueFieldChange = useDebouncedCallback(debouncedRef, (valueField: string) => {
    useVerbaPagamentoListaFilter.setState({ valueField });
    return async () => {
      try {
        setLoading(true);
        useVerbaPagamentoListaPagination.setState({ page: 1 });
        await loadVerbaPagamentos({ valueField, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Error", processError(e, "Falha ao buscar verba de pagamentos!"));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadVerbaPagamentos,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useVerbaPagamentoListaPagination.setState({ page: 1 });
      await loadVerbaPagamentos({ page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Error", processError(e, "Falha ao buscar verba de pagamentos!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadVerbaPagamentos,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useVerbaPagamentoListaPagination.setState({ page: 1, rowsPerPage });
      await loadVerbaPagamentos({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Error", processError(e, "Falha ao buscar verba de pagamentos!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadVerbaPagamentos,
    pushErrorNotification,
  ]);

  const handleOpenChangeHistory = useCallback(() => {
    if (selectedVerbaPagamento) {
      changeHistory.handleOpenChangeHistory(selectedVerbaPagamento.idItemVerba, LogTipoTabela.ITEM_VERBA, false);
    }
  }, [
    selectedVerbaPagamento,
    changeHistory,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    verbaPagamentos: verbaPagamentos ?? [],
    selectedVerbaPagamento: selectedVerbaPagamento ?? null,
    openCadastroModal,
    openDeleteModal,
    filters,
    pagination,
    changeHistory,
    handleSubmitVerbaPagamento,
    handleReload,
    handleCadastroVerbaPagamentoOpenState,
    handleDeleteVerbaPagamentoOpenState,
    handleConfirmarDeletarVerbaPagamento,
    handleTipoPesquisaChange,
    handleValueFieldChange,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenChangeHistory,
  };
}